import apiService from './api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { ApiResponse } from '@/types/api';

export interface DocPageRef {
  id: number;
  title: string;
  slug: string;
  order: number;
}

export interface DocSectionRef {
  id: number;
  title: string;
  slug: string;
  order: number;
  pages: DocPageRef[];
}

export interface DocPageContentBlock {
  type: 'text' | 'image';
  content?: string;
  imageUrl?: string;
  imagePosition?: 'left' | 'right' | 'full';
  imageAlt?: string;
}

export interface DocPageData {
  id: number;
  sectionId: number;
  title: string;
  slug: string;
  content: string;
}

export const DocsService = {
  list(): Promise<DocSectionRef[]> {
    return apiService.get<ApiResponse<DocSectionRef[]>>(API_ENDPOINTS.DOCS.LIST).then((r) => r.data.data || []);
  },

  getPage(sectionSlug: string, pageSlug: string): Promise<DocPageData> {
    return apiService
      .get<ApiResponse<DocPageData>>(API_ENDPOINTS.DOCS.PAGE(sectionSlug, pageSlug))
      .then((r) => r.data.data as DocPageData);
  },

  uploadImage(file: File): Promise<{ url: string }> {
    const form = new FormData();
    form.append('file', file);
    return apiService
      .post<ApiResponse<{ url: string }>>(API_ENDPOINTS.DOCS.UPLOAD_IMAGE, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((r) => ({ url: r.data.data?.url || '' }));
  },

  // Admin
  adminListSections(): Promise<Array<DocSectionRef & { DocPages?: Array<{ id: number; title: string; slug: string; order: number; content?: string }> }>> {
    return apiService
      .get<ApiResponse<any[]>>(API_ENDPOINTS.DOCS.ADMIN_SECTIONS)
      .then((r) => r.data.data || []);
  },

  adminCreateSection(payload: { title: string; slug: string; order?: number }) {
    return apiService.post(API_ENDPOINTS.DOCS.ADMIN_SECTIONS, payload);
  },

  adminUpdateSection(id: number, payload: { title?: string; slug?: string; order?: number }) {
    return apiService.put(API_ENDPOINTS.DOCS.ADMIN_SECTION(id), payload);
  },

  adminDeleteSection(id: number) {
    return apiService.delete(API_ENDPOINTS.DOCS.ADMIN_SECTION(id));
  },

  adminCreatePage(payload: { sectionId: number; title: string; slug: string; order?: number; content?: string }) {
    return apiService.post(API_ENDPOINTS.DOCS.ADMIN_PAGES, payload);
  },

  adminUpdatePage(id: number, payload: { title?: string; slug?: string; order?: number; content?: string }) {
    return apiService.put(API_ENDPOINTS.DOCS.ADMIN_PAGE(id), payload);
  },

  adminDeletePage(id: number) {
    return apiService.delete(API_ENDPOINTS.DOCS.ADMIN_PAGE(id));
  }
};
